import React, { useEffect, useState, useMemo } from "react";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Button, Badge, Table, Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
    Pagination, PaginationItem, PaginationLink, Spinner
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import Chart from "react-apexcharts";
import moment from "moment";
import CountUp from "react-countup";
import { useFeedbackStore } from "../../stores/Feedback";
import { useSiteStore } from "../../stores/Assets/site";
import { useSpaceStore } from "../../stores/Assets/space";
import { tokenHelper } from "../../stores/helpers/token_helper";
import "./Feedback.css";

const KPICard = ({ title, value, trend, trendValue, icon, color, decimals = 0, suffix = "" }) => (
    <Card className="card-animate">
        <CardBody>
            <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0">{title}</p>
                </div>
            </div>
            <div className="d-flex align-items-end justify-content-between mt-4">
                <div>
                    <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <CountUp
                            start={0}
                            end={value}
                            duration={3}
                            decimals={decimals}
                            suffix={suffix}
                        />
                    </h4>
                    {trendValue && (
                        <p className={`mb-0 text-${trend === 'up' ? 'success' : 'danger'}`}>
                            <span className={`badge bg-${trend === 'up' ? 'success' : 'danger'}-subtle text-${trend === 'up' ? 'success' : 'danger'} mb-0`}>
                                <i className={trend === 'up' ? 'ri-arrow-right-up-line' : 'ri-arrow-right-down-line'}></i> {trendValue}
                            </span>
                        </p>
                    )}
                </div>
                <div className="avatar-sm flex-shrink-0">
                    <span className={`avatar-title rounded fs-3 bg-${color}-subtle text-${color}`}>
                        <i className={icon}></i>
                    </span>
                </div>
            </div>
        </CardBody>
    </Card>
);

const SkeletonKPICard = () => (
    <Card className="card-animate">
        <CardBody>
            <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                    <div className="skeleton skeleton-text" style={{ width: '40%' }}></div>
                </div>
            </div>
            <div className="d-flex align-items-end justify-content-between mt-4">
                <div className="flex-grow-1">
                    <div className="skeleton skeleton-title" style={{ width: '60%', height: '30px' }}></div>
                    <div className="skeleton skeleton-text" style={{ width: '30%' }}></div>
                </div>
                <div className="skeleton skeleton-avatar"></div>
            </div>
        </CardBody>
    </Card>
);

const SkeletonTable = () => (
    <div className="p-4">
        {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="d-flex gap-3 mb-3">
                <div className="skeleton" style={{ width: '50px', height: '20px' }}></div>
                <div className="skeleton flex-grow-1" style={{ height: '20px' }}></div>
                <div className="skeleton" style={{ width: '100px', height: '20px' }}></div>
                <div className="skeleton" style={{ width: '80px', height: '20px' }}></div>
            </div>
        ))}
    </div>
);

const FeedbackDashboard = () => {
    document.title = "Customer Feedback | Analytics";

    // Filters State
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);
    const [selectedRatings, setSelectedRatings] = useState([]);
    const [dateRange, setDateRange] = useState([
        moment().subtract(7, "days").toDate(),
        new Date(),
    ]);
    const [spaceSort, setSpaceSort] = useState("Highest first");
    const [userName, setUserName] = useState("User");

    // Store Hooks
    const {
        dashboardData,
        feedbacks,
        pagination,
        isLoading,
        isTableLoading,
        fetchDashboardData,
        fetchFeedbacks
    } = useFeedbackStore();

    const { getSites, sites } = useSiteStore();
    const { getSpaces, spaces } = useSpaceStore();

    useEffect(() => {
        getSites();
        handleFilterUpdate(); // Initial Load
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            const user = await tokenHelper.getUser();
            if (user && user.name) {
                setUserName(user.name);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (selectedSiteIds.length > 0) {
            getSpaces({ siteIds: selectedSiteIds });
        }
    }, [selectedSiteIds]);

    const handleFilterUpdate = () => {
        fetchDashboardData({
            siteIds: selectedSiteIds,
            spaceIds: selectedSpaceIds,
            startDate: dateRange[0],
            endDate: dateRange[1],
            ratings: selectedRatings,
        });
    };

    const handlePageChange = (page) => {
        fetchFeedbacks({
            siteIds: selectedSiteIds,
            spaceIds: selectedSpaceIds,
            startDate: dateRange[0],
            endDate: dateRange[1],
            ratings: selectedRatings,
            page: page,
            limit: 10
        });
    };

    const handleRatingClick = (rating) => {
        const ratings = [];
        for (let i = 1; i <= rating; i++) {
            ratings.push(i);
        }
        setSelectedRatings(ratings);
        // Special case: Distribution click DOES trigger immediate filter for UX
        fetchDashboardData({
            siteIds: selectedSiteIds,
            spaceIds: selectedSpaceIds,
            startDate: dateRange[0],
            endDate: dateRange[1],
            ratings: ratings,
        });
    };

    const clearRatingFilter = () => {
        setSelectedRatings([]);
        handleFilterUpdate();
    };

    // removed automatic useEffect filter

    // Trend Chart Config
    const trendChartOptions = {
        chart: {
            height: 350,
            type: 'area',
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3, colors: ['#6366f1'] },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.4,
                opacityTo: 0.1,
                stops: [0, 90, 100],
                colorStops: [
                    { offset: 0, color: "#6366f1", opacity: 0.4 },
                    { offset: 100, color: "#6366f1", opacity: 0.1 },
                ]
            }
        },
        xaxis: {
            type: 'datetime',
            categories: dashboardData?.trend?.map(t => t.date) || [],
            labels: { style: { colors: "#878a99", fontSize: '12px' } },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            min: 1,
            max: 5,
            tickAmount: 4,
            labels: { style: { colors: "#878a99" } }
        },
        grid: {
            borderColor: "#f1f1f1",
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } }
        },
        tooltip: { x: { format: 'dd MMM' } }
    };

    // Distribution Data Processing
    const totalReviews = dashboardData?.kpis?.totalFeedbacks || 0;
    const distribution = dashboardData?.distribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const positiveRate = dashboardData?.kpis?.positiveRate || 0;

    const getDistPercent = (val) => totalReviews > 0 ? (val / totalReviews) * 100 : 0;

    const distColors = {
        5: "#22c55e",
        4: "#4ade80",
        3: "#facc15",
        2: "#f97316",
        1: "#ef4444"
    };

    const kpis = dashboardData?.kpis || {};

    return (
        <React.Fragment>
            <div className="feedback-modern">
                <Container fluid className="px-lg-5">

                    <Row className="mb-4 pb-1">
                        <Col xs={12}>
                            <div className="d-flex align-items-lg-center flex-lg-row flex-column">
                                <div className="flex-grow-1">
                                    <h4 className="fs-16 mb-1">Hello {userName}!</h4>
                                    <p className="text-muted mb-0">Feedback Overview</p>
                                </div>
                                <div className="mt-3 mt-lg-0">
                                    <div className="d-flex gap-2 align-items-center flex-wrap">
                                        {selectedRatings.length > 0 && (
                                            <Button color="link" className="text-danger text-decoration-none p-0 me-2" onClick={clearRatingFilter}>
                                                Clear Rating Filter
                                            </Button>
                                        )}
                                        <div style={{ minWidth: "200px" }}>
                                            <Select
                                                isMulti
                                                options={sites?.map(s => ({ label: s.name, value: s.site_id }))}
                                                placeholder="All Locations"
                                                className="modern-select shadow-sm"
                                                onChange={(sel) => setSelectedSiteIds(sel?.map(s => s.value) || [])}
                                            />
                                        </div>
                                        <div style={{ width: "240px" }}>
                                            <Flatpickr
                                                className="form-control border-0 shadow-sm px-3"
                                                style={{ height: "42px", borderRadius: "8px" }}
                                                options={{ mode: "range", dateFormat: "Y-m-d" }}
                                                value={dateRange}
                                                onChange={setDateRange}
                                            />
                                        </div>
                                        <Button className="px-4 shadow-sm btn-main" style={{ height: "42px", borderRadius: "8px" }} onClick={handleFilterUpdate} disabled={isLoading}>
                                            {isLoading ? <Spinner size="sm" /> : "Filter"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* KPI Summary Rows */}
                    <Row className="mb-4 g-4">
                        {isLoading ? (
                            [1, 2, 3, 4].map(i => (
                                <Col xl={3} md={6} key={i}>
                                    <SkeletonKPICard />
                                </Col>
                            ))
                        ) : (
                            <>
                                <Col xl={3} md={6}>
                                    <KPICard
                                        title="Average Rating"
                                        value={kpis.averageRating || 0}
                                        trend={(kpis.ratingChange || 0) >= 0 ? "up" : "down"}
                                        trendValue={Math.abs(kpis.ratingChange || 0).toString().startsWith('0') ? kpis.ratingChange : `+${kpis.ratingChange}`}
                                        icon="ri-star-line"
                                        color="main"
                                        decimals={1}
                                        suffix="★"
                                    />
                                </Col>
                                <Col xl={3} md={6}>
                                    <KPICard
                                        title="Total Feedbacks"
                                        value={totalReviews}
                                        trend="up"
                                        trendValue="+12.5%"
                                        icon="ri-bar-chart-line"
                                        color="main"
                                    />
                                </Col>
                                <Col xl={3} md={6}>
                                    <KPICard
                                        title="With Comments"
                                        value={kpis.commentPercentage || 0}
                                        trend="down"
                                        trendValue="-2.1%"
                                        icon="ri-chat-3-line"
                                        color="main"
                                        suffix="%"
                                    />
                                </Col>
                                <Col xl={3} md={6}>
                                    <KPICard
                                        title="Positive Rate"
                                        value={positiveRate}
                                        trend="up"
                                        trendValue="+1.8%"
                                        icon="ri-thumb-up-line"
                                        color="main"
                                        suffix="%"
                                    />
                                </Col>
                            </>
                        )}
                    </Row>

                    {/* Average Rating Over Time */}
                    <Card>
                        <CardHeader className="d-flex align-items-center">
                            <h4 className="card-title mb-0 flex-grow-1">Average Rating Over Time</h4>
                            <div className="period-toggle d-flex bg-light p-1 rounded">
                                <span className="active px-2 py-1 fs-12 cursor-pointer">Day</span>
                                <span className="px-2 py-1 fs-12 cursor-pointer text-muted">Week</span>
                                <span className="px-2 py-1 fs-12 cursor-pointer text-muted">Month</span>
                            </div>
                        </CardHeader>
                        <CardBody style={{ minHeight: '350px' }}>
                            {isLoading ? (
                                <div className="skeleton w-100 h-100" style={{ minHeight: '350px' }}></div>
                            ) : (
                                dashboardData?.trend?.length > 0 ? (
                                    <Chart
                                        options={trendChartOptions}
                                        series={[{ name: "Avg Rating", data: dashboardData.trend.map(t => t.rating) }]}
                                        type="area"
                                        height={350}
                                    />
                                ) : (
                                    <div className="py-5 text-center text-muted">No data for the selected period</div>
                                )
                            )}
                        </CardBody>
                    </Card>

                    {/* Detailed Analysis */}
                    <Row className="g-4 mb-5">
                        <Col lg={6}>
                            <Card className="h-100">
                                <CardHeader>
                                    <h4 className="card-title mb-0">Rating Distribution</h4>
                                </CardHeader>
                                <CardBody>
                                    {isLoading ? (
                                        <div className="py-2">
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <div key={i} className="skeleton skeleton-text mb-4" style={{ height: '24px' }}></div>
                                            ))}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="distribution-list">
                                                {[5, 4, 3, 2, 1].map(r => (
                                                    <div key={r} className="distribution-row d-flex align-items-center mb-3 cursor-pointer" onClick={() => handleRatingClick(r)}>
                                                        <span className={`fs-13 fw-medium ${selectedRatings.includes(r) ? 'text-primary' : 'text-muted'}`} style={{ width: '30px' }}>{r}★</span>
                                                        <div className="flex-grow-1 mx-3">
                                                            <div className="progress rounded-pill" style={{ height: '24px', backgroundColor: '#f1f3f5' }}>
                                                                <div
                                                                    className="progress-bar rounded-pill"
                                                                    style={{
                                                                        width: `${getDistPercent(distribution[r])}%`,
                                                                        backgroundColor: distColors[r],
                                                                        transition: 'width 1s ease-in-out',
                                                                        opacity: selectedRatings.length > 0 && !selectedRatings.includes(r) ? 0.3 : 1
                                                                    }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                        {selectedRatings.includes(r) && <i className="ri-check-line text-primary fw-bold"></i>}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                                <span className="text-muted fs-13">Total: {totalReviews.toLocaleString()} reviews</span>
                                                <span className="fs-13 fw-medium d-flex align-items-center gap-1">
                                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }}></span>
                                                    {positiveRate}% positive (4-5★)
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={6}>
                            <Card className="h-100">
                                <CardHeader className="d-flex align-items-center">
                                    <h4 className="card-title mb-0 flex-grow-1">Rating by Space</h4>
                                    <div className="dropdown modern-dropdown">
                                        <button className="btn btn-light btn-sm dropdown-toggle px-3" type="button">
                                            {spaceSort} <i className="ri-arrow-down-s-line ms-1"></i>
                                        </button>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {isLoading ? (
                                        <div className="py-2">
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <div key={i} className="skeleton skeleton-text mb-4" style={{ height: '28px' }}></div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="space-comparison-list">
                                            {(dashboardData?.spaceComparison || []).slice(0, 5).map((s, i) => (
                                                <div key={i} className="mb-4">
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fs-13 fw-medium text-muted">{s.name}</span>
                                                    </div>
                                                    <div className="progress rounded-pill" style={{ height: '28px', backgroundColor: '#f1f3f5' }}>
                                                        <div
                                                            className="progress-bar rounded-pill"
                                                            style={{
                                                                width: `${(s.rating / 5) * 100}%`,
                                                                backgroundColor: s.rating >= 4 ? '#22c55e' : (s.rating >= 3 ? '#facc15' : '#ef4444')
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Feedback History Table */}
                    <Card className="mb-5">
                        <CardHeader className="d-flex align-items-center">
                            <h4 className="card-title mb-0 flex-grow-1">Customer Feedback History</h4>
                            <div className="d-flex align-items-center gap-2">
                                {selectedRatings.length > 0 && (
                                    <Badge color="primary-subtle" className="text-primary rounded-pill px-3 py-1">
                                        Rating ≤ {Math.max(...selectedRatings)} ★
                                    </Badge>
                                )}
                                {(isLoading || isTableLoading) && <Spinner size="sm" color="primary" />}
                            </div>
                        </CardHeader>
                        <CardBody className="p-0">
                            {(isLoading || isTableLoading) ? (
                                <SkeletonTable />
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="bg-light-subtle">
                                                <tr>
                                                    <th className="px-4 py-3 text-muted fs-12 text-uppercase fw-bold">Rating</th>
                                                    <th className="py-3 text-muted fs-12 text-uppercase fw-bold">Comment</th>
                                                    <th className="py-3 text-muted fs-12 text-uppercase fw-bold">Location</th>
                                                    <th className="px-4 py-3 text-muted fs-12 text-uppercase fw-bold text-end">Time</th>
                                                </tr>
                                            </thead>
                                            <tbody className="border-0">
                                                {feedbacks?.length > 0 ? (
                                                    feedbacks.map((f, i) => (
                                                        <tr key={i} className="border-0 hover-bg-light transition-all">
                                                            <td className="px-4 py-3">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className={`rating-circle ${f.rating >= 4 ? 'bg-success' : (f.rating >= 3 ? 'bg-warning' : 'bg-danger')}`}></div>
                                                                    <span className="fw-bold">{f.rating} ★</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3">
                                                                <div className={`text-break ${!f.comment ? 'text-muted opacity-50 fst-italic' : 'text-dark fw-medium'}`}>
                                                                    {f.comment || "No comment left by customer"}
                                                                </div>
                                                            </td>
                                                            <td className="py-3">
                                                                <span className="badge bg-light text-muted border-0 py-2 px-3">{f.space?.name || "Main Space"}</span>
                                                            </td>
                                                            <td className="px-4 py-3 text-end text-muted fs-13">
                                                                {moment(f.created_at).fromNow()}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-5">
                                                            <div className="opacity-50">
                                                                <i className="ri-feedback-line fs-24 mb-2 d-block"></i>
                                                                No feedback records found.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </CardBody>
                        {pagination && pagination.totalPages > 1 && (
                            <CardHeader className="bg-transparent border-0 p-4 d-flex justify-content-center">
                                <Pagination Aria-label="Page navigation example">
                                    <PaginationItem disabled={pagination.currentPage === 1}>
                                        <PaginationLink previous onClick={() => handlePageChange(pagination.currentPage - 1)} />
                                    </PaginationItem>
                                    {[...Array(pagination.totalPages)].map((_, i) => (
                                        <PaginationItem active={i + 1 === pagination.currentPage} key={i}>
                                            <PaginationLink onClick={() => handlePageChange(i + 1)}>
                                                {i + 1}
                                            </PaginationLink>
                                        </PaginationItem>
                                    ))}
                                    <PaginationItem disabled={pagination.currentPage === pagination.totalPages}>
                                        <PaginationLink next onClick={() => handlePageChange(pagination.currentPage + 1)} />
                                    </PaginationItem>
                                </Pagination>
                            </CardHeader>
                        )}
                    </Card>

                </Container>
            </div>
        </React.Fragment>
    );
};

export default FeedbackDashboard;
